import { Controller } from "@hotwired/stimulus";

interface BoardPoint {
  x: number;
  y: number;
}

interface BoardStroke {
  points: BoardPoint[];
}

interface BoardDrawingData {
  version: 1;
  width: 800;
  height: 600;
  lineWidth: 6;
  strokes: BoardStroke[];
}

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;
const LINE_WIDTH = 6;
const MIN_POINT_DISTANCE = 2;

export default class BoardCanvasController extends Controller<HTMLElement> {
  static targets = ["canvas", "clearButton", "submitButton", "toast"];
  static values = {
    seat: Number,
    submitUrl: String,
    requestToken: String,
  };

  declare readonly canvasTarget: HTMLCanvasElement;
  declare readonly clearButtonTarget: HTMLButtonElement;
  declare readonly submitButtonTarget: HTMLButtonElement;
  declare readonly toastTarget: HTMLElement;
  declare readonly seatValue: number;
  declare readonly submitUrlValue: string;
  declare readonly requestTokenValue: string;

  private canvasContext: CanvasRenderingContext2D | null = null;
  private strokes: BoardStroke[] = [];
  private currentStroke: BoardStroke | null = null;
  private activePointerId: number | null = null;
  private submitted = false;
  private toastTimerId: number | null = null;

  connect(): void {
    this.canvasContext = this.canvasTarget.getContext("2d");
    this.setupCanvasContext();
    this.resizeCanvas();
    this.canvasTarget.addEventListener("pointerdown", this.startStroke);
    this.canvasTarget.addEventListener("pointermove", this.extendStroke);
    this.canvasTarget.addEventListener("pointerup", this.endStroke);
    this.canvasTarget.addEventListener("pointercancel", this.endStroke);
    window.addEventListener("resize", this.resizeCanvas);
    // iOS Safari は viewport と touch-action だけではピンチズームを抑止できないため、
    // 画面全体が固定サイズであるボード解答ページの表示中だけブラウザジェスチャをキャンセルする。
    document.addEventListener("gesturestart", this.preventPinchZoom, { passive: false });
    document.addEventListener("touchmove", this.preventPinchZoomOnMultiTouch, { passive: false });
    // touch-action: none と user-select 系CSSだけでは、iPad Safari の長押しテキスト選択ジェスチャー
    // （虫めがねルーペ・選択ドラッグ）がまれにすり抜ける。Touch Events 仕様上 touchstart を
    // キャンセルすればネイティブジェスチャー全体を抑止でき、Pointer Events による描画には影響しない。
    // 副作用として canvas 起点の click 合成イベントも消えるが、このキャンバスは描画専用なので問題ない。
    this.canvasTarget.addEventListener("touchstart", this.preventNativeTouchGestures, { passive: false });
  }

  disconnect(): void {
    this.canvasTarget.removeEventListener("pointerdown", this.startStroke);
    this.canvasTarget.removeEventListener("pointermove", this.extendStroke);
    this.canvasTarget.removeEventListener("pointerup", this.endStroke);
    this.canvasTarget.removeEventListener("pointercancel", this.endStroke);
    window.removeEventListener("resize", this.resizeCanvas);
    document.removeEventListener("gesturestart", this.preventPinchZoom);
    document.removeEventListener("touchmove", this.preventPinchZoomOnMultiTouch);
    this.canvasTarget.removeEventListener("touchstart", this.preventNativeTouchGestures);
    if (this.toastTimerId !== null) {
      window.clearTimeout(this.toastTimerId);
      this.toastTimerId = null;
    }
  }

  resizeCanvas = (): void => {
    const scale = window.devicePixelRatio || 1;
    const displayWidth = this.canvasTarget.clientWidth || CANVAS_WIDTH;
    const displayHeight = this.canvasTarget.clientHeight || CANVAS_HEIGHT;

    this.canvasTarget.width = Math.round(displayWidth * scale);
    this.canvasTarget.height = Math.round(displayHeight * scale);
    this.setupCanvasContext();
    this.redraw();
  };

  drawingData(): BoardDrawingData {
    return {
      version: 1,
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
      lineWidth: LINE_WIDTH,
      strokes: this.strokes,
    };
  }

  clear(): void {
    this.strokes = [];
    this.currentStroke = null;
    this.activePointerId = null;
    this.setSubmitted(false);
    this.redraw();
    this.showResetToast();
  }

  async submit(): Promise<void> {
    this.setSubmitted(true);

    try {
      await fetch(this.submitUrlValue, {
        method: "POST",
        headers: this.requestHeaders(),
        body: JSON.stringify({
          seat: this.seatValue,
          drawing_data: this.drawingData(),
        }),
      });
    } catch {
      // 送信失敗は無視する（ポーリングで再試行可能）
    }
  }

  private startStroke = (event: PointerEvent): void => {
    if (this.currentStroke || this.submitted) return;

    event.preventDefault();
    this.canvasTarget.setPointerCapture(event.pointerId);
    const point = this.toCanvasPoint(event);
    this.currentStroke = { points: [point] };
    this.activePointerId = event.pointerId;
    this.strokes.push(this.currentStroke);
    this.redraw();
  };

  private extendStroke = (event: PointerEvent): void => {
    if (!this.currentStroke || event.pointerId !== this.activePointerId) return;

    event.preventDefault();
    const point = this.toCanvasPoint(event);
    const previousPoint = this.currentStroke.points.at(-1);
    if (previousPoint && distance(previousPoint, point) < MIN_POINT_DISTANCE) return;

    this.currentStroke.points.push(point);
    this.redraw();
  };

  private endStroke = (event: PointerEvent): void => {
    if (!this.currentStroke || event.pointerId !== this.activePointerId) return;

    event.preventDefault();
    this.canvasTarget.releasePointerCapture(event.pointerId);
    this.currentStroke = null;
    this.activePointerId = null;
  };

  private preventPinchZoom = (event: Event): void => {
    event.preventDefault();
  };

  private preventPinchZoomOnMultiTouch = (event: TouchEvent): void => {
    if (event.touches.length > 1) event.preventDefault();
  };

  private preventNativeTouchGestures = (event: Event): void => {
    event.preventDefault();
  };

  private toCanvasPoint(event: PointerEvent): BoardPoint {
    const rect = this.canvasTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * CANVAS_WIDTH;
    const y = ((event.clientY - rect.top) / rect.height) * CANVAS_HEIGHT;
    return { x: roundCoordinate(x), y: roundCoordinate(y) };
  }

  private setupCanvasContext(): void {
    if (!this.canvasContext) return;

    const scaleX = this.canvasTarget.width / CANVAS_WIDTH;
    const scaleY = this.canvasTarget.height / CANVAS_HEIGHT;
    this.canvasContext.setTransform(scaleX, 0, 0, scaleY, 0, 0);
    this.canvasContext.lineCap = "round";
    this.canvasContext.lineJoin = "round";
    this.canvasContext.strokeStyle = "#000000";
    this.canvasContext.lineWidth = LINE_WIDTH;
  }

  private redraw(): void {
    if (!this.canvasContext) return;

    this.canvasContext.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    for (const stroke of this.strokes) {
      if (stroke.points.length === 0) continue;

      const firstPoint = stroke.points[0] as BoardPoint;

      this.canvasContext.beginPath();
      if (stroke.points.length === 1) {
        // Canvas のゼロ長パスは描画されないため、タップを線幅と同じ直径の円として描く。
        this.canvasContext.arc(firstPoint.x, firstPoint.y, LINE_WIDTH / 2, 0, Math.PI * 2);
        this.canvasContext.fill();
        continue;
      }

      this.canvasContext.moveTo(firstPoint.x, firstPoint.y);
      for (const point of stroke.points.slice(1)) {
        this.canvasContext.lineTo(point.x, point.y);
      }
      this.canvasContext.stroke();
    }
  }

  private setSubmitted(submitted: boolean): void {
    this.submitted = submitted;
    this.submitButtonTarget.disabled = submitted;
    this.element.classList.toggle("is-submitted", submitted);
  }

  private showResetToast(): void {
    const toast = this.toastTarget;

    if (this.toastTimerId !== null) {
      window.clearTimeout(this.toastTimerId);
      this.toastTimerId = null;
    }

    toast.classList.remove("is-entering", "is-leaving");
    // アニメーションを確実にリスタートするためリフローを強制する
    void toast.offsetHeight;
    toast.classList.add("is-entering");

    this.toastTimerId = window.setTimeout(() => {
      this.toastTimerId = null;
      toast.classList.remove("is-entering");
      void toast.offsetHeight;
      toast.classList.add("is-leaving");
    }, 2000);
  }

  private requestHeaders(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      "X-Board-Request-Token": this.requestTokenValue,
    };
  }
}

function distance(a: BoardPoint, b: BoardPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function roundCoordinate(value: number): number {
  return Math.round(value * 100) / 100;
}
